  import React, { useState } from 'react';
  import { useNavigate } from 'react-router-dom';
  import { toast } from 'react-toastify';
  import { Box, Button, Container, IconButton, InputAdornment, Stack, TextField, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
  import ForgotPasswordModal from './modals/forgotPasswordModal';
  import RegistrationModal from './modals/registrationModal';
  import Styles from '../common/styles/styles';
  import { Visibility, VisibilityOff } from '@mui/icons-material';
  import { useUser } from '../../userContext';

  function Login() {
    const navigate = useNavigate();
    const [inputUserName, setInputUserName] = useState('');
    const [password, setPassword] = useState('');
    const { userName, updateUserId, updateUserName, updateUserRole, updateCcgEmployee, updateUserRegistrationDate } = useUser();
    const [showPasswords, setShowPasswords] = useState({ password: false });
    
    // Email verification states
    const [showEmailVerification, setShowEmailVerification] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [pendingUserName, setPendingUserName] = useState('');
    const [isResendingCode, setIsResendingCode] = useState(false);

    // Email registration unverified states
    const [showUnverifiedDialog, setShowUnverifiedDialog] = useState(false);
    const [unverifiedUserName, setUnverifiedUserName] = useState('');

    const textFieldStyles = Styles.textFieldStyles;

    const validateUser = async () => {
      const loginData = { 
        userName: inputUserName,
        password: password,
      };
      
      try {
        const response = await fetch((`${process.env.REACT_APP_API}/users/login`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(loginData),
        });
        const result = await response.json();
        
        if (result.message === 'success') {
          updateUserId(result.user.id); 
          updateUserName(inputUserName); 
          updateUserRole(result.groupName);
          updateCcgEmployee(result.user.ccg_employee);
          if (result.user.ccg_employee) {
            const date = new Date(); 
            date.setDate(date.getDate());
            const futureDate = date.toISOString();
            updateUserRegistrationDate(futureDate);
          } else {
            updateUserRegistrationDate(result.user.registration_dt);
          }
          navigate('/terms'); 
        } else if ((result.message === 'email_not_verified') || (result.message === 'email_verification_expired')) {
        
          setPendingUserName(inputUserName);
          setShowEmailVerification(true);
          
          switch (result.message) {
              case 'email_not_verified':
                  toast.info('Please verify your email address to continue.');
                  break;
              case 'email_verification_expired':
                  toast.info('Email Verification Code Expired.  Please Check Your Email For The New Code Prior To Verifying This User.');
                  break;
              default:
                  toast.error('Error. Please try again: ');
          }
        } else if (result.message === 'registration_lapsed') {
          toast.info('Registration Time Has Lasped.  You Are Only Allowed To Utilize This System For 45 Days.');
        } else {
          toast.error('Invalid username / password combination. Please try again');
        }
      } catch (error) {
        toast.error('Network error. Please try again: ', error);
      }
    };

    const sendVerificationEmail = async (userName) => {
      try {
        setIsResendingCode(true);
        const response = await fetch((`${process.env.REACT_APP_API}/users/send-verification`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userName }),
        });
        const result = await response.json();
        
        if (result.message === 'success') {
          toast.success('Verification Email Sent');
        } else {
          toast.error('Failed to send verification email. Please try again.');
        }
      } catch (error) {
        toast.error('Network error while sending verification email: ', error);
      } finally {
        setIsResendingCode(false);
      }
    };

    const verifyEmail = async () => {
      if (!verificationCode.trim()) {
        toast.error('Please enter the verification code.');
        return;
      }

      try {
        const response = await fetch((`${process.env.REACT_APP_API}/users/verify-email`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            userName: pendingUserName, 
            verificationCode: verificationCode.trim() 
          }),
        });
        const result = await response.json();
        
        if (result.message === 'success') {
          toast.success('Email Verified');
          setShowEmailVerification(false);
          setVerificationCode('');
          updateUserName(pendingUserName);
          navigate('/terms');
        } else if (result.message === 'invalid_code') {
          toast.error('Invalid verification code. Please try again.');
        } else if (result.message === 'code_expired') {
          toast.error('Verification code has expired. Please request a new one.');
        } else {
          toast.error('Email verification failed. Please try again.');
        }
      } catch (error) {
        toast.error('Network error during email verification: ', error);
      }
    };

    const handleCloseVerification = (event) => {
      event.preventDefault();
      const target = event.target.tagName;
      if ( target === 'BUTTON' ) {
        setShowEmailVerification(false);
        setVerificationCode('');
        setPendingUserName('');
      }
    };

    const handleCloseUnverifiedDialog = () => {
      setShowUnverifiedDialog(false);
      setUnverifiedUserName('');
    };

    const handleResendCode = () => {
      sendVerificationEmail(pendingUserName);
    };

    const handleResendCodeForUnverified = () => {
      sendVerificationEmail(unverifiedUserName);
      setShowUnverifiedDialog(false);
      setPendingUserName(unverifiedUserName);
      setShowEmailVerification(true);
    };

    const togglePasswordVisibility = (field) => {
      setShowPasswords(prev => ({
        ...prev,
        [field]: !prev[field]
      }));
    };

    const handleLogin = (event) => {
      event.preventDefault();
      validateUser();
    };

    const handleVerificationSubmit = (event) => {
      event.preventDefault();
      verifyEmail();
    };

  // Function to handle when the 
  const handleRegistrationResponse = (result) => {
    if (result.message === 'email_registered_unverified') {
      toast.info('This email is registered but not verified.');
    }
  };
    
    if (userName) {
      return <p>Welcome back, {userName}!</p>;
    }

    return (
      <>
        <Container component="main" maxWidth="xs" sx={{ mt: 15 }}>
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography component="h1" variant="h2" m={2}>
              CheckMate
            </Typography>
            <Typography component="h5" variant="h5" mb={2}>
              <Box>Powered by Phen.AI</Box>
            </Typography>
            <Box component="form" onSubmit={handleLogin}>
              <Stack>
                  <TextField
                      required
                      fullWidth
                      label="User Name"
                      value={inputUserName}
                      onChange={(e) => setInputUserName(e.target.value)}              
                      sx={textFieldStyles}                 
                  />
              </Stack>
              <Stack sx={{ mt: 4 }}>
                  <TextField
                    required
                    fullWidth
                    label="Password"
                    type={showPasswords.password ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}              
                    sx={textFieldStyles}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility('password')}
                              edge="end"
                              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                            >
                              {showPasswords.password ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    }}
                  />
              </Stack>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 4, bgcolor:"#303F9F" }}            
              >
                Log In
              </Button>
              <ForgotPasswordModal />
              <RegistrationModal onRegistrationResponse={handleRegistrationResponse} />
            </Box>
          </Box>
        </Container>

        {/* Email Verification Dialog */}
        <Dialog 
          open={showEmailVerification} 
          onClose={handleCloseVerification}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6">
              Email Verification Required
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Please check your email for a verification code and enter it below to complete your log in.
            </Typography>
            <Box component="form" onSubmit={handleVerificationSubmit}>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                sx={{ mb: 2 }}
                inputProps={{ 
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.5rem' }
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Didn't receive the code? 
              <Button 
                variant="text" 
                size="small" 
                onClick={handleResendCode}
                disabled={isResendingCode}
                sx={{ ml: 1, textTransform: 'none' }}
              >
                {isResendingCode ? 'Sending...' : 'Resend Code'}
              </Button>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseVerification}>
              Cancel
            </Button>
            <Button 
              onClick={verifyEmail}
              variant="contained"
              disabled={!verificationCode.trim()}
            >
              Verify Email
            </Button>
          </DialogActions>
        </Dialog>

        {/* Unverified Account Dialog */}
        <Dialog 
          open={showUnverifiedDialog} 
          onClose={handleCloseUnverifiedDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6">
              Account Found - Verification Required
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              This email address is already registered under the username <strong>{unverifiedUserName}</strong>, but the account has not been verified yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Would you like to verify your existing account or use a different email address?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUnverifiedDialog}>
              Use Different Email
            </Button>
            <Button 
              onClick={handleResendCodeForUnverified}
              variant="contained"
              color="primary"
            >
              Verify Existing Account
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  export default Login;